import { createJSONStorage, type PersistStorage } from 'zustand/middleware';

interface WrappedValue {
  value: string;
  expiresAt: number;
}

const isBrowserEnvironment = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export function createTTLJSONStorage<T>(ttlMs: number): PersistStorage<T> {
  const memoryStorage = new Map<string, WrappedValue>();

  const getStore = () => ({
    getItem: (name: string) => {
      const now = Date.now();

      if (isBrowserEnvironment) {
        let raw: string | null = null;
        try {
          raw = window.localStorage.getItem(name);
        } catch {
          raw = null;
        }
        if (!raw) {
          return null;
        }

        try {
          const parsed = JSON.parse(raw) as Partial<WrappedValue>;
          if (typeof parsed.expiresAt === 'number' && parsed.expiresAt <= now) {
            window.localStorage.removeItem(name);
            return null;
          }
          if (typeof parsed.value === 'string') {
            return parsed.value;
          }
        } catch {
          window.localStorage.removeItem(name);
          return null;
        }

        window.localStorage.removeItem(name);
        return null;
      }

      const entry = memoryStorage.get(name);
      if (!entry) {
        return null;
      }
      if (entry.expiresAt <= now) {
        memoryStorage.delete(name);
        return null;
      }
      return entry.value;
    },
    setItem: (name: string, value: string) => {
      const expiresAt = Date.now() + ttlMs;

      if (isBrowserEnvironment) {
        try {
          window.localStorage.setItem(name, JSON.stringify({ value, expiresAt }));
          return;
        } catch {
          // fallback to memory storage below
        }
      }

      memoryStorage.set(name, { value, expiresAt });
    },
    removeItem: (name: string) => {
      if (isBrowserEnvironment) {
        try {
          window.localStorage.removeItem(name);
          return;
        } catch {
          // fallback to memory storage below
        }
      }

      memoryStorage.delete(name);
    }
  });

  const storage = createJSONStorage<T>(getStore);
  if (!storage) {
    throw new Error('Failed to create TTL storage');
  }
  return storage;
}
